import React, { useState } from "react";
import { X, Check, Link as LinkIcon } from "lucide-react";
import { useTheme, WALLPAPERS } from "./ThemeContext";

export default function SettingsPanel({ open, onClose }) {
  const { theme, setTheme, wallpaper, setWallpaper } = useTheme();
  const [customUrl, setCustomUrl] = useState("");

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
      data-testid="settings-panel"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}
      />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative w-[440px] max-w-full h-full crm-glass-strong border-l flex flex-col"
        style={{ color: "var(--crm-text)" }}
      >
        <header
          className="flex items-center justify-between px-6 py-5 border-b"
          style={{ borderColor: "var(--crm-line-soft)" }}
        >
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "var(--crm-accent)" }}>
              Settings
            </div>
            <h2 className="text-xl font-light mt-1">Personalise</h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full crm-glass flex items-center justify-center"
          >
            <X size={15} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {/* Theme */}
          <section>
            <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-muted)" }}>
              Appearance
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "dark", label: "Dark", grad: "linear-gradient(135deg, #0d1428 0%, #1a2648 100%)" },
                { id: "light", label: "Light", grad: "linear-gradient(135deg, #f4f5fa 0%, #e6e9f3 100%)" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t.id)}
                  data-testid={`theme-${t.id}`}
                  className="rounded-2xl p-4 text-left border transition"
                  style={{
                    background: t.grad,
                    borderColor: theme === t.id ? "var(--crm-accent)" : "var(--crm-line)",
                    color: t.id === "light" ? "#111" : "#fff",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t.label}</span>
                    {theme === t.id && (
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-black"
                        style={{ background: "var(--crm-accent)" }}
                      >
                        <Check size={12} />
                      </span>
                    )}
                  </div>
                  <div className="mt-6 text-[10px] tracking-widest uppercase opacity-60">
                    {t.id === "dark" ? "Cinematic" : "Daylight"}
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Wallpaper */}
          <section>
            <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-muted)" }}>
              Wallpaper
            </div>
            <div className="grid grid-cols-3 gap-3">
              {WALLPAPERS.map((w) => {
                const active = wallpaper === w.url || (!wallpaper && w.id === "none");
                return (
                  <button
                    key={w.id}
                    onClick={() => setWallpaper(w.url)}
                    data-testid={`wallpaper-${w.id}`}
                    className="relative aspect-[4/3] rounded-xl overflow-hidden border transition"
                    style={{
                      borderColor: active ? "var(--crm-accent)" : "var(--crm-line)",
                    }}
                  >
                    {w.url ? (
                      <img
                        src={w.url}
                        alt={w.label}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950" />
                    )}
                    {active && (
                      <div
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-black"
                        style={{ background: "var(--crm-accent)" }}
                      >
                        <Check size={11} />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 text-white text-[10px] px-2 py-1 bg-black/45 truncate">
                      {w.label}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Custom URL */}
            <div className="mt-4">
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--crm-text-muted)" }}>
                Or paste a URL
              </label>
              <div className="mt-2 flex gap-2">
                <div className="flex-1 relative">
                  <LinkIcon
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: "var(--crm-text-subtle)" }}
                  />
                  <input
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="https://…"
                    data-testid="wallpaper-url"
                    className="w-full pl-9 pr-3 py-2 rounded-lg text-sm crm-pane focus:outline-none"
                    style={{ background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
                  />
                </div>
                <button
                  onClick={() => {
                    if (customUrl.trim()) {
                      setWallpaper(customUrl.trim());
                      setCustomUrl("");
                    }
                  }}
                  className="px-3 py-2 rounded-lg text-sm text-black font-medium"
                  style={{
                    background:
                      "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)",
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}
